import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Pagination as MuiPagination,
  Paper,
  Tooltip,
  TextField,
  Avatar,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Inventory as ContainerIcon,
} from '@mui/icons-material';
import { useContainerCore } from '../../../../core/modules/container';
import { useMasterCore } from '../../../../core/modules/master';
import { useCompanyCore } from '../../../../core/modules/company';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { Container } from '../../../../core/interfaces/container.interface';
import { ContainerCategory } from '../../../../core/interfaces/category.interface';
import { LocateContainerFilters } from './LocateContainerFilters';
import { DESTINY_CONTAINER_TYPE_LABEL, DESTINY_CONTAINER_TYPE_COLOR } from '../../../../core/constants/containerTypes';

interface LocateContainerListProps {
  onOpenLocateDialog: () => void;
  selectedContainerIds: number[];
  onContainerSelect: (containerId: number) => void;
  onSelectAll: (containerIds: number[]) => void;
  onContainersLoaded: (containers: Container[]) => void;
  onClearSelection: () => void;
}

export const LocateContainerList: React.FC<LocateContainerListProps> = ({
  onOpenLocateDialog,
  selectedContainerIds,
  onContainerSelect,
  onSelectAll,
  onContainersLoaded,
  onClearSelection,
}) => {
  const {
    containers,
    total,
    options,
    getContainers,
    setOptions,
  } = useContainerCore();
  const { containerCategories, containerTypes, channelTypes, priceRanges, getPriceRanges, getTypes } = useMasterCore();
  const { companyId } = useCompanyCore();
  const { locations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();

  const [searchCode, setSearchCode] = useState('');
  const [sortConfig, setSortConfig] = useState<{ field: 'code' | 'status' | 'category' | 'type' | 'channel' | 'price' | 'location'; direction: 'asc' | 'desc' }>({ field: 'code', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(false);

  // Cargar contenedores al montar el componente
  useEffect(() => {
    if (companyId) {
      setIsLoading(true);
      getContainers(companyId, options).finally(() => {
        setIsLoading(false);
      });
    }
  }, [companyId, getContainers, options]);

  useEffect(() => {
    if (companyId) {
      getWarehouses(companyId);
    }
  }, [companyId, getWarehouses]);

  useEffect(() => {
    if (companyId) {
      getPriceRanges(companyId);
    }
  }, [companyId, getPriceRanges]);

  useEffect(() => {
    if (companyId) {
      getTypes(companyId, 'channel');
    }
  }, [companyId, getTypes]);

  // Notificar cuando se cargan los contenedores
  useEffect(() => {
    if (containers) {
      onContainersLoaded(containers);
    }
  }, [containers, onContainersLoaded]);

  const handleSelectAll = () => {
    if (containers) {
      const containerIds = containers.map(c => c.id);
      onSelectAll(containerIds);
    }
  };

  const handleContainerSelect = (containerId: number) => {
    onContainerSelect(containerId);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    const newOptions = { ...options, page: newPage };
    setOptions(newOptions);
    setIsLoading(true);
    getContainers(companyId!, newOptions).finally(() => {
      setIsLoading(false);
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchCode(value);
    const newOptions = { ...options, code: value || undefined, page: 1 };
    setOptions(newOptions);
    setIsLoading(true);
    getContainers(companyId!, newOptions).finally(() => {
      setIsLoading(false);
    });
  };


  const handleLocateSelected = () => {
    if (selectedContainerIds.length > 0) {
      onOpenLocateDialog();
    }
  };

  const handleLocateIndividual = (container: Container) => {
    onContainerSelect(container.id);
    onOpenLocateDialog();
  };

  const getTotalPages = () => {
    if (!total) return 1;
    return Math.ceil(total / 15); // Default page size of 15
  };

  const currentPageContainerIds = containers?.map(c => c.id) || [];
  const selectedInCurrentPage = currentPageContainerIds.filter(id => selectedContainerIds.includes(id));
  
  const isAllSelected = Boolean(
    containers &&
      containers.length > 0 &&
      selectedInCurrentPage.length === containers.length
  );
  const isIndeterminate =
    selectedInCurrentPage.length > 0 &&
    selectedInCurrentPage.length < (containers?.length || 0);

  // Helper functions para obtener nombres
  const getCategoryName = (categoryId: number) => {
    const category = containerCategories?.find(cat => cat.id === categoryId);
    if (!category) return '-';
    const acronym = category.acronym ? `(${category.acronym}) - ` : '';
    return `${acronym}${category.name}`;
  };

  const getTypeName = (categoryId: number) => {
    const category = containerCategories?.find(cat => cat.id === categoryId) as ContainerCategory | undefined;
    if (!category) return '-';
    const type = containerTypes?.find(type => type.id === category.container_type_id);
    if (!type) return '-';
    const acronym = (type as { acronym?: string }).acronym ? `(${(type as { acronym?: string }).acronym}) - ` : '';
    return `${acronym}${type.name}`;
  };

  const getChannelName = (channelId: number) => {
    if (!channelId) return '-';
    return channelTypes?.find(channel => channel.id === channelId)?.name || '-';
  };

  const getPriceRangeText = (priceRangeId: number) => {
    const priceRange = priceRanges?.find(range => range.id === priceRangeId);
    return priceRange ? `$${priceRange.start_price.toLocaleString()} - $${priceRange.end_price.toLocaleString()}` : '-';
  };

  const warehouseMap = useMemo(() => {
    const map = new Map<number, string>();
    warehouses?.forEach((warehouse) => {
      map.set(warehouse.id, warehouse.code);
    });
    return map;
  }, [warehouses]);

  const getLocationLabel = (locationId?: number | null) => {
    if (!locationId) {
      return 'Sin ubicación';
    }
    const location = locations?.find(loc => loc.id === locationId);
    if (!location) {
      return 'Sin ubicación';
    }
    const warehouseCode = warehouseMap.get(location.warehouse_id) || 'SIN BODEGA';
    return `${location.code} - ${warehouseCode}`;
  };

  const getStatusName = (statusId: number) => {
    return DESTINY_CONTAINER_TYPE_LABEL[statusId] || '-';
  };

  const getStatusColor = (statusId: number) => {
    return DESTINY_CONTAINER_TYPE_COLOR[statusId] || 'default';
  };

  const categoryMap = useMemo(() => {
    const map = new Map<number, string>();
    containerCategories?.forEach((category) => {
      const acronym = category.acronym ? `(${category.acronym}) - ` : '';
      map.set(category.id, `${acronym}${category.name}`);
    });
    return map;
  }, [containerCategories]);

  const typeMap = useMemo(() => {
    const map = new Map<number, string>();
    containerTypes?.forEach((type) => {
      const acronym = (type as { acronym?: string }).acronym ? `(${(type as { acronym?: string }).acronym}) - ` : '';
      map.set(type.id, `${acronym}${type.name}`);
    });
    return map;
  }, [containerTypes]);

  const sortedContainers = useMemo(() => {
    if (!containers) return [] as Container[];
    const items = [...containers];
    const directionFactor = sortConfig.direction === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      const compare = (valA: string | number | null | undefined, valB: string | number | null | undefined) => {
        if (valA === valB) return 0;
        if (valA === undefined || valA === null) return -directionFactor;
        if (valB === undefined || valB === null) return directionFactor;
        if (typeof valA === 'number' && typeof valB === 'number') {
          return valA < valB ? -directionFactor : directionFactor;
        }
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        if (strA === strB) return 0;
        return strA < strB ? -directionFactor : directionFactor;
      };

      switch (sortConfig.field) {
        case 'code':
          return compare(a.code, b.code);
        case 'status':
          return compare(getStatusName(a.container_status_id), getStatusName(b.container_status_id));
        case 'category':
          return compare(categoryMap.get(a.container_category_id), categoryMap.get(b.container_category_id));
        case 'type':
          return compare(getTypeName(a.container_category_id), getTypeName(b.container_category_id));
        case 'channel':
          return compare(getChannelName(a.channel_type_id ?? 0), getChannelName(b.channel_type_id ?? 0));
        case 'price':
          return compare(getPriceRangeText(a.price_range_id ?? 0), getPriceRangeText(b.price_range_id ?? 0));
        case 'location':
          return compare(getLocationLabel(a.location_id ?? undefined), getLocationLabel(b.location_id ?? undefined));
        default:
          return 0;
      }
    });

    return items;
  }, [containers, sortConfig, categoryMap, typeMap, channelTypes, priceRanges, locations, warehouseMap]);

  const handleSort = (field: typeof sortConfig.field) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { field, direction: 'asc' };
    });
  };

  if (!containers && total === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography>
              No hay contenedores disponibles para ubicar
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">
            Contenedores disponibles para ubicar ({total || 0})
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedContainerIds.length > 0 && (
              <Chip
                label={`${selectedContainerIds.length} seleccionados`}
                color="primary"
                variant="outlined"
                onDelete={onClearSelection}
              />
            )}
            <IconButton
              color="primary"
              disabled={selectedContainerIds.length === 0}
              onClick={handleLocateSelected}
              size="large"
            >
              <Tooltip title="Ubicar seleccionados">
                <LocationIcon />
              </Tooltip>
            </IconButton>
          </Box>
        </Box>

        {/* Búsqueda rápida y filtros */}
        <Box display="flex" alignItems="center" mb={2} gap={2}>
          <Box flex={1}>
            <TextField
              fullWidth
              label="Buscar por código"
              placeholder="Ingrese el código del contenedor..."
              value={searchCode}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '56px', // Same height as the button
                }
              }}
            />
          </Box>
          <Box sx={{ height: '56px', display: 'flex', alignItems: 'center' }}>
            <LocateContainerFilters
              filters={options}
              onFiltersChange={(newFilters) => {
                const updatedOptions = { ...options, ...newFilters };
                setOptions(updatedOptions);
              }}
              onApplyFilters={() => {
                setIsLoading(true);
                getContainers(companyId!, options).finally(() => {
                  setIsLoading(false);
                });
              }}
              onClearFilters={() => {
                const clearedOptions = { ...options, page: 1 };
                delete clearedOptions.code;
                delete clearedOptions.status;
                delete clearedOptions.category;
                delete clearedOptions.location_id;
                delete clearedOptions.priceRange;
                delete clearedOptions.recipientType;
                setOptions(clearedOptions);
                setIsLoading(true);
                getContainers(companyId!, clearedOptions).finally(() => {
                  setIsLoading(false);
                });
              }}
            />
          </Box>
        </Box>


            <TableContainer component={Paper} variant="outlined" sx={{ position: 'relative' }}>
              {isLoading && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px"
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bgcolor="rgba(255, 255, 255, 0.8)"
                  zIndex={1}
                >
                  <CircularProgress />
                </Box>
              )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={!containers || containers.length === 0}
                  />
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'code' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'code'}
                    direction={sortConfig.field === 'code' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('code')}
                  >
                    Contenedor
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'status' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'status'}
                    direction={sortConfig.field === 'status' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'category' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'category'}
                    direction={sortConfig.field === 'category' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Categoría
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'type' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'type'}
                    direction={sortConfig.field === 'type' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'channel' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'channel'}
                    direction={sortConfig.field === 'channel' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('channel')}
                  >
                    Canal
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'price' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'price'}
                    direction={sortConfig.field === 'price' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('price')}
                  >
                    Rango de Precio
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig.field === 'location' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.field === 'location'}
                    direction={sortConfig.field === 'location' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('location')}
                  >
                    Ubicación Actual
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContainers.map((container: Container) => (
                <TableRow
                  key={container.id}
                  hover
                  selected={selectedContainerIds.includes(container.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedContainerIds.includes(container.id)}
                      onChange={() => handleContainerSelect(container.id)}
                    />
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{ 
                          width: 40,
                          height: 40,
                          backgroundColor: 'grey.200',
                          color: 'primary.main',
                        }}
                      >
                        <ContainerIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                          {container.code || 'SIN CÓDIGO'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {container.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={getStatusName(container.container_status_id)}
                      color={getStatusColor(container.container_status_id)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {getCategoryName(container.container_category_id)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {getTypeName(container.container_category_id)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {getChannelName(container.channel_type_id ?? 0)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {getPriceRangeText(container.price_range_id ?? 0)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {getLocationLabel(container.location_id)}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title={selectedContainerIds.length > 1 ? 'Disponible solo cuando seleccionas un contenedor' : 'Ubicar contenedor'}>
                      <span>
                        <IconButton
                          color="primary"
                          onClick={() => handleLocateIndividual(container)}
                          size="small"
                          disabled={selectedContainerIds.length > 1}
                        >
                          <LocationIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {(!containers || containers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchCode
                        ? `No se encontraron contenedores con el código "${searchCode}"`
                        : 'No se encontraron contenedores para ubicar'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total && total > 0 && getTotalPages() > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <MuiPagination
              count={getTotalPages()}
              page={options.page || 1}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
